import React from "react";
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Box, 
  Button, 
  Stack,
  useTheme
} from "@mui/material";
import moment from "moment-timezone";
import { isFutureBookingInOrgTimezone } from "../utils/timezoneUtils";
import CalendarIcon from "../assets/calender-icon.svg";
import TimeIcon from "../assets/time-icon.svg";
import NameIcon from "../assets/name-icon.svg";
import { Image as ImageIcon } from "@mui/icons-material";

// BookingCard component with RoomCard styling patterns
export default function BookingCard({ 
  booking, 
  onCancel, 
  isAdmin = false, 
  orgTimeZone = "UTC",
  showEdit = false,
  onEdit = null
}) {
  const theme = useTheme();

  if (!booking) return null;

  // Format date and time
  const formatDate = (dateTime) => {
    return moment.tz(dateTime, orgTimeZone).format("dddd DD, MMMM YYYY");
  };

  const formatTime = (startTime, endTime) => {
    const start = moment.tz(startTime, orgTimeZone).format("hh:mm A");
    const end = moment.tz(endTime, orgTimeZone).format("hh:mm A");
    return `${start} - ${end}`;
  };

  const formatCreatedDate = (createdAt) => {
    return moment.tz(createdAt, orgTimeZone).format("dddd DD, MMMM YYYY");
  };

  // Get room image or use placeholder (prefer full resolution like RoomCard)
  const roomImage = booking.room?.images && booking.room.images.length > 0 
    ? booking.room.images[0].url || booking.room.images[0].thumbnailUrl 
    : null;

  return (
    <Card
      sx={{
        maxWidth: 390,
        minWidth: 320,
        height: "100%",
        borderRadius: theme.shape.borderRadius * 1.5,
        border: `1px solid ${theme.palette.divider}`,
        p: { xs: 1, sm: 1.25 },
        pb: { xs: 1, sm: 1.25 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Room Image */}
        {roomImage ? (
          <CardMedia
            component="img"
            sx={{
              height: { xs: 180, sm: 228 },
              width: "100%",
              borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
              objectFit: 'cover',
              backgroundColor: theme.palette.primary.light + '1A',
            }}
            image={roomImage}
            alt={`${booking.room?.name || 'Meeting room'} image`}
          />
        ) : (
          <Box
            sx={{
              height: { xs: 180, sm: 228 },
              width: "100%",
              backgroundColor: theme.palette.primary.light + '1A',
              borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.secondary
            }}
          >
            <ImageIcon sx={{ fontSize: 48 }} />
          </Box>
        )}

        <CardContent sx={{ 
          p: 0, 
          pl: 1, 
          pt: 2.5,
          width: "100%", 
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }}>
          {/* Top content: Title and Details */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: theme.typography.h5.fontWeight,
                color: theme.palette.text.primary,
                fontSize: theme.typography.h5.fontSize,
                mb: 2.5,
                wordWrap: 'break-word',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {booking.room?.name || "Meeting Room"}
            </Typography>

            <Stack spacing={1.5} sx={{ pl: 0.5, width: "100%", mb: 2.5 }}>
              {/* Date */}
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  component="img"
                  src={CalendarIcon}
                  alt="Calendar"
                  sx={{ 
                    width: 24, 
                    height: 15,
                    flexShrink: 0,
                    mt: 0.25,
                    filter: `brightness(0) saturate(100%) invert(${theme.palette.mode === 'dark' ? '100%' : '0%'})`
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: theme.typography.body1.fontWeight,
                      color: theme.palette.text.primary,
                      fontSize: theme.typography.body1.fontSize
                    }}
                  >
                    Date:{" "}
                  </Typography>
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: theme.typography.body1.fontWeight,
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.body1.fontSize
                    }}
                  >
                    {formatDate(booking.startTime)}
                  </Typography>
                </Box>
              </Stack>

              {/* Time */}
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  component="img"
                  src={TimeIcon}
                  alt="Time"
                  sx={{ 
                    width: 24, 
                    height: 15,
                    flexShrink: 0,
                    mt: 0.25,
                    filter: `brightness(0) saturate(100%) invert(${theme.palette.mode === 'dark' ? '100%' : '0%'})`
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: theme.typography.body1.fontWeight,
                      color: theme.palette.text.primary,
                      fontSize: theme.typography.body1.fontSize
                    }}
                  >
                    Meeting time:{" "}
                  </Typography>
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: theme.typography.body1.fontWeight,
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.body1.fontSize
                    }}
                  >
                    {formatTime(booking.startTime, booking.endTime)}
                  </Typography>
                </Box>
              </Stack>

              {/* Meeting Name */}
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  component="img"
                  src={NameIcon}
                  alt="Name"
                  sx={{ 
                    width: 24, 
                    height: 15,
                    flexShrink: 0,
                    mt: 0.25,
                    filter: `brightness(0) saturate(100%) invert(${theme.palette.mode === 'dark' ? '100%' : '0%'})`
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: theme.typography.body1.fontWeight,
                      color: theme.palette.text.primary,
                      fontSize: theme.typography.body1.fontSize
                    }}
                  >
                    Meeting Name:{" "}
                  </Typography>
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: theme.typography.body1.fontWeight,
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.body1.fontSize
                    }}
                  >
                    {booking.meetingName || "Untitled Meeting"}
                  </Typography>
                </Box>
              </Stack>

              {/* Admin: Show booked by */}
              {isAdmin && booking.user && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    component="img"
                    src={NameIcon}
                    alt="User"
                    sx={{ 
                      width: 24, 
                      height: 15,
                      flexShrink: 0,
                      mt: 0.25,
                      filter: `brightness(0) saturate(100%) invert(${theme.palette.mode === 'dark' ? '100%' : '0%'})`
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      component="span" 
                      sx={{ 
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: theme.typography.body1.fontWeight,
                        color: theme.palette.text.primary,
                        fontSize: theme.typography.body1.fontSize
                      }}
                    >
                      Booked by:{" "}
                    </Typography>
                    <Typography 
                      component="span" 
                      sx={{ 
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: theme.typography.body1.fontWeight,
                        color: theme.palette.text.secondary,
                        fontSize: theme.typography.body1.fontSize
                      }}
                    >
                      {booking.user.username || "Unknown User"}
                    </Typography>
                  </Box>
                </Stack>
              )}

              {/* Date Created */}
              <Box sx={{ pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Typography 
                  sx={{ 
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: theme.typography.body2.fontWeight,
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body2.fontSize
                  }}
                >
                  Date Created: {formatCreatedDate(booking.createdAt)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Bottom content: Action buttons */}
          <Stack 
            direction="row"  
            alignItems="center" 
            sx={{
              flexWrap: 'wrap',
              gap: 1,
              minHeight: 43,
            }}
          >
            {showEdit && onEdit && isFutureBookingInOrgTimezone(booking.endTime, orgTimeZone) && (
              <Button
                variant="outlined"
                onClick={() => onEdit(booking)}
                sx={{
                  minWidth: 80,
                  height: 43,
                  borderRadius: theme.shape.borderRadius * 0.5,
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.button.fontWeight,
                  fontSize: theme.typography.button.fontSize,
                  textTransform: theme.typography.button.textTransform,
                  transition: theme.transitions.create(
                    ['background-color', 'transform'],
                    { duration: theme.transitions.duration.short }
                  ),
                  "&:hover": {
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Edit
              </Button>
            )}
            
            {onCancel && (
              <Button
                variant="contained"
                onClick={() => onCancel(booking._id)}
                sx={{
                  flex: '1 1 auto',
                  maxWidth: 193,
                  minWidth: 140,
                  height: 43,
                  backgroundColor: theme.palette.error.main,
                  borderRadius: theme.shape.borderRadius * 0.5,
                  boxShadow: theme.customShadows?.errorButton || theme.shadows[3],
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: theme.typography.button.fontWeight,
                  fontSize: theme.typography.button.fontSize,
                  textTransform: theme.typography.button.textTransform,
                  transition: theme.transitions.create(
                    ['background-color', 'transform'],
                    { duration: theme.transitions.duration.short }
                  ),
                  "&:hover": {
                    backgroundColor: theme.palette.error.dark,
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Cancel Booking
              </Button>
            )}
            
          </Stack>
        </CardContent>
      </Box>
    </Card>
  );
} 